import { Permission } from 'app/(secured)/(robot)/[robotId]/settings/access/types';

import {
  DeviceName,
  MyRobotDTO,
  MyRobotsPrimaryInformationResponse,
  MyRobotsPrimaryInformationResponseDTO,
  ROBOT_MODEL,
} from 'types/models';
import { PayloadItem } from 'types/models/Payload';

import { getToken } from '../../authProvider';
import { axiosRequest } from '../axios';
import { robotImageByModelName } from '../robot-template';
import { getPermissionQueryString } from './const';
import { CreateConfigFileDTO } from './types';

export type AddMyRobotRequestDTO = {
  // We use a subset of the MyRobotDTO because ui doesn't need all the fields.
  // Note: Somehow it is currently not known by myself
  // why the api types have no requires and also db types have no requires
  // myRobot: MyRobotDTO;
  myRobot: {
    iotHubDeviceName?: string;
    robotTemplateId: number;
    myRobotToPayloads: {
      isActive: boolean;
      payloadId: number;
    }[];
  };
  // AdditionalRobotInformations: AdditionalRobotInformationsType;
};

export const MyRobotUrls = {
  GetRobotDataConfig: {
    // Deprecated
    method: 'get',
    url: (robotId: number) => `myRobot/Get/${robotId}`,
  },
  PrimaryInformation: {
    method: 'get',
    url: 'myRobot/PrimaryInformation',
  },
  GetMyRobotPayloads: {
    method: 'get',
    url: (robotId: number) => `myRobot/Payloads/${robotId}`,
  },
  CreateConfigFile: {
    method: 'post',
    url: (deviceName: DeviceName) => `myRobot/${deviceName}/config`,
  },
  GetMyRobotPrimaryInformation: {
    method: 'get',
    url: (robotId: number) => `myRobot/PrimaryInformation/${robotId}`,
  },
  GetMyRobotToPayloadsForImages: {
    method: 'get',
    url: (robotId: number) => `myRobot/GetMyRobotToPayloadsForImages/${robotId}`,
  },
  AddMyRobot: {
    method: 'post',
    url: 'myRobot/AddMyRobot',
  },
  Delete: {
    method: 'delete',
    url: (robotId: number) => `myRobot/Delete/${robotId}`,
  },
  GetUsers: {
    method: 'get',
    url: (robotId: number) => `myRobot/${robotId}/Users`,
  },
  AppendUserPermission: {
    method: 'post',
    url: (robotId: number, userId: number, permission: Permission) => {
      const queryString = getPermissionQueryString(permission);
      return `myRobot/${robotId}/Users/${userId}?${queryString}`;
    },
  },
  DeleteUserPermission: {
    method: 'delete',
    url: (robotId: number, userId: number, permission: Permission) => {
      const queryString = getPermissionQueryString(permission);
      return `myRobot/${robotId}/Users/${userId}?${queryString}`;
    },
  },
} as const;

export function extendRobotPrimaryInformation(
  robot: MyRobotsPrimaryInformationResponse,
): MyRobotsPrimaryInformationResponseDTO {
  const modelName = robot.modelName as ROBOT_MODEL;
  const imageUrl = robotImageByModelName(modelName);

  return {
    ...robot,
    imageUrl,
    modelName,
  };
}

export class MyRobotManager {
  static async GetMyRobot(robotId: number): Promise<MyRobotDTO> {
    const { method, url } = MyRobotUrls.GetRobotDataConfig;
    const { data } = await axiosRequest<MyRobotDTO>(method, url(robotId));
    return data;
  }

  static async getAllRobotPrimaryInformation(): Promise<MyRobotsPrimaryInformationResponseDTO[]> {
    const { method, url } = MyRobotUrls.PrimaryInformation;
    const { data } = await axiosRequest<MyRobotsPrimaryInformationResponse[]>(method, url);
    return data.map(extendRobotPrimaryInformation);
  }

  static async GetMyRobotPayloads(myRobotId: number): Promise<PayloadItem[]> {
    const { method, url } = MyRobotUrls.GetMyRobotPayloads;
    const { data } = await axiosRequest<PayloadItem[]>(method, url(myRobotId));
    return data;
  }

  static async GetMyRobotPrimaryInformation(
    robotId: number,
  ): Promise<MyRobotsPrimaryInformationResponseDTO> {
    const { method, url } = MyRobotUrls.GetMyRobotPrimaryInformation;
    const { data } = await axiosRequest<MyRobotsPrimaryInformationResponse>(method, url(robotId));
    return extendRobotPrimaryInformation(data);
  }

  static async GetMyRobotToPayloadsForImages(robotId: number): Promise<PayloadItem[]> {
    const { method, url } = MyRobotUrls.GetMyRobotToPayloadsForImages;
    const { data } = await axiosRequest<PayloadItem[]>(method, url(robotId));
    return data;
  }

  static async AddMyRobot(body: AddMyRobotRequestDTO): Promise<boolean> {
    const { method, url } = MyRobotUrls.AddMyRobot;
    const { data } = await axiosRequest<boolean>(method, url, body);
    return data;
  }

  static async Delete(robotId: number) {
    const { method, url } = MyRobotUrls.Delete;
    const { data } = await axiosRequest<string>(method, url(robotId));
    // success is defined as 200 response and no error message in string
    return data.length === 0;
  }

  // returns a stream for download
  static async CreateConfigFile(deviceName: DeviceName, body: CreateConfigFileDTO) {
    const { method, url } = MyRobotUrls.CreateConfigFile;

    const fullUrl = `${global.clientConfig.BackendUrl}/${url(deviceName)}`;
    return fetch(fullUrl, {
      method,
      headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  static async GetUsers(robotId: number) {
    const { method, url } = MyRobotUrls.GetUsers;

    const { data } = await axiosRequest<string>(method, url(robotId));
    return data;
  }

  static async AppendUserPermission(robotId: number, userId: number, permission: Permission) {
    const { method, url } = MyRobotUrls.AppendUserPermission;

    const { data } = await axiosRequest<string>(method, url(robotId, userId, permission));
    return data;
  }

  static async DeleteUserPermission(robotId: number, userId: number, permission: Permission) {
    const { method, url } = MyRobotUrls.DeleteUserPermission;

    const { data } = await axiosRequest<string>(method, url(robotId, userId, permission));
    return data;
  }
}
